import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase, isSupabaseReady } from "@/integrations/supabase/client";
import { linkTo, getImageUrl, getPublicUrl } from "@/lib/utils";
import { getPageTitle } from "@/utils/PageUtils";
import StickyNavigation from "@/components/layout/StickyNavigation";
import TenantFooter from "@/components/layout/TenantFooter";
import { BLOG_SECTIONS, sectionToSlug } from "@/lib/blogSections";

interface MockPost {
  slug: string;
  title: string;
  excerpt: string;
  cover: string;
  author: string;
  date: string;
  tags: string[];
  section?: string;
}

const mockPosts: MockPost[] = [
  {
    slug: "introducing-our-ai-risk-engine",
    title: "Introducing our AI Risk Engine",
    excerpt: "How we leverage ML to automate risk scoring for SMEs.",
    cover: getImageUrl("placeholder.svg"),
    author: "YC Tech Team",
    date: "2025-09-21",
    tags: ["AI", "Risk", "Engineering"],
  },
  {
    slug: "building-a-quote-pipeline",
    title: "Building a Quote Pipeline at Scale",
    excerpt: "Architecture notes on scaling multi-tenant quote flows.",
    cover: getImageUrl("placeholder.svg"),
    author: "Platform",
    date: "2025-08-14",
    tags: ["Architecture", "Cloud"],
  },
  {
    slug: "ux-for-business-insurance",
    title: "UX for Business Insurance",
    excerpt: "Design principles we use to reduce friction in journeys.",
    cover: getImageUrl("placeholder.svg"),
    author: "Design",
    date: "2025-07-02",
    tags: ["UX", "Design"],
  },
];
type PublicPost = {
  slug: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  author_name: string | null;
  section?: string | null;
  published_at: string | null;
  tags: string[] | null;
};

export default function BlogIndexPage() {
  const [posts, setPosts] = useState<MockPost[]>([]);

  useEffect(() => {
    document.title = getPageTitle("The Chronicle");
    let ignore = false;
    async function load() {
      try {
        if (!isSupabaseReady) return;
        const { data, error } = await supabase
          .from('blog_posts')
          .select('slug, title, excerpt, cover_image_url, author_name, section, published_at, tags')
          .eq('status', 'published')
          .order('published_at', { ascending: false });
        if (error) throw error;
        if (!ignore && data) {
          const mapped = (data as any[]).map((row) => ({
            slug: row.slug,
            title: row.title,
            excerpt: row.excerpt ?? '',
            cover: (row.cover_image_url && String(row.cover_image_url).trim()) || getImageUrl('placeholder.svg'),
            author: row.author_name ?? 'YC Tech Team',
            date: row.published_at ?? new Date().toISOString(),
            tags: Array.isArray(row.tags) ? row.tags : [],
            // keep section for future layout/filtering (not shown in card yet)
            section: row.section || 'Business',
          })) as MockPost[];
          setPosts(mapped);
        }
      } catch (e) {
        // fall back silently to mock
      }
    }
    load();
    return () => { ignore = true; };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <StickyNavigation />
      <style>{`
        @keyframes ticker {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .ticker-anim { animation: ticker 14s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .ticker-anim { animation: none !important; } }
        /* Hide scrollbars on mobile overflow menus */
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>

      {/* Sections nav (top) */}
      <div className="sticky top-[64px] sm:top-[80px] z-40 border-b bg-gradient-to-b from-blue-50/90 to-blue-100/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Compact spacing on md+; scrollable on mobile */}
          <nav className="hidden md:flex items-center justify-center gap-x-4 md:gap-x-6 py-3 text-sm">
            <Link to={linkTo(`/chronicle`)} className="text-black hover:text-blue-700">Home</Link>
            <Link to={linkTo(`/chronicle/section/${sectionToSlug('Top Stories')}`)} className="text-black hover:text-blue-700">Top Stories</Link>
            {BLOG_SECTIONS.filter((s) => s !== 'Top Stories').map((s) => (
              <Link key={s} to={linkTo(`/chronicle/section/${sectionToSlug(s)}`)} className="text-black hover:text-blue-700">
                {s}
              </Link>
            ))}
          </nav>
          <nav className="md:hidden no-scrollbar flex gap-4 overflow-x-auto py-3 text-sm snap-x snap-mandatory -mx-4 px-4">
            <Link to={linkTo(`/chronicle`)} className="text-black whitespace-nowrap shrink-0 snap-start">Home</Link>
            <Link to={linkTo(`/chronicle/section/${sectionToSlug('Top Stories')}`)} className="text-black whitespace-nowrap shrink-0 snap-start">Top Stories</Link>
            {BLOG_SECTIONS.filter((s) => s !== 'Top Stories').map((s) => (
              <Link key={s} to={linkTo(`/chronicle/section/${sectionToSlug(s)}`)} className="text-black whitespace-nowrap shrink-0 snap-start">
                {s}
              </Link>
            ))}
          </nav>
        </div>
      </div>


      {/* Hero */}
      <section className="border-b" style={{ backgroundColor: '#36336c' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16 sm:h-20 md:h-28 lg:h-32">
            <img
              src={getPublicUrl('/Logos/new/newlogo.png')}
              alt="The Chronicle"
              className="object-contain h-[75%] max-w-full"
              onError={(e) => {
                const img = e.currentTarget as HTMLImageElement;
                // 1) Try base-aware URL (e.g. /uae/en/business/Logos/...)
                if (!img.dataset.tryBase) {
                  img.dataset.tryBase = '1';
                  img.src = getPublicUrl('/Logos/new/newlogo.png');
                  return;
                }
                // 2) Fallback to root-absolute URL (/Logos/...)
                if (!img.dataset.tryRoot) {
                  img.dataset.tryRoot = '1';
                  img.src = '/Logos/new/newlogo.png';
                  return;
                }
                // 3) Fallback to Supabase public storage URL
                if (!img.dataset.trySupabase) {
                  img.dataset.trySupabase = '1';
                  img.src = 'https://mborlyertmbornltlmdf.supabase.co/storage/v1/object/public/insurer-logos/newlogo.png';
                  return;
                }
                // 4) Final fallback to bundled placeholder
                img.onerror = null;
                img.src = getImageUrl('yallacompare-for-business-logo.png');
              }}
            />
          </div>
        </div>
      </section>


      {/* Posts grid with Top Stories featured layout */}
      <section className="flex-1">
        {(() => {
          const all = posts.length ? posts : mockPosts;
          const topStories = all.filter((p) => (p.section || '') === 'Top Stories');
          const mainTop = topStories[0];
          const nonTop = all.filter((p) => (p.section || '') !== 'Top Stories');
          const leftPosts = nonTop.slice(0, 2);
          const rightPosts = nonTop.slice(2, 4);
          const usedSlugs = new Set<string>([
            ...(mainTop ? [mainTop.slug, ...leftPosts.map((p) => p.slug), ...rightPosts.map((p) => p.slug)] : []),
          ]);
          const remainingPosts = all.filter((p) => !usedSlugs.has(p.slug));

          return (
            <>
              {mainTop && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
                    {/* Left column (2 smaller articles stacked) */}
                    <div className="lg:col-span-3 hidden lg:flex flex-col gap-6">
                      {leftPosts.map((lp) => (
                        <article key={lp.slug} className="group rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                          <Link to={linkTo(`/chronicle/${lp.slug}`)}>
                            <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                              <img
                                src={lp.cover}
                                alt={lp.title}
                                loading="lazy"
                                decoding="async"
                                fetchPriority="low"
                                className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform"
                                onError={(e) => {
                                  const img = e.currentTarget as HTMLImageElement;
                                  img.onerror = null;
                                  img.src = getImageUrl('placeholder.svg');
                                  img.style.display = 'block';
                                }}
                              />
                            </div>
                            <div className="p-4">
                              <h3
                                className="text-base font-semibold text-gray-900 group-hover:text-blue-700"
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                              >
                                {(lp.section || '') === 'Top Stories' && (
                                  <span className="inline-flex items-baseline align-baseline mr-2 text-red-600 font-semibold text-xs leading-none whitespace-nowrap">
                                    <span className="inline-block w-2 h-2 rounded-full bg-red-600 mr-1 animate-pulse relative -top-[1px]"></span>
                                    LIVE
                                  </span>
                                )}
                                {lp.title}
                              </h3>
                              <p
                                className="mt-1 text-gray-600 text-sm"
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                              >
                                {lp.excerpt || ''}
                              </p>
                              <div className="mt-3 text-xs text-gray-500 flex items-center justify-between">
                                <span>{lp.author}</span>
                                <span>{new Date(lp.date).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </Link>
                        </article>
                      ))}
                    </div>

                    {/* Main Top Story (big and central) */}
                    <div className="lg:col-span-6">
                      <article className="group rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden h-full">
                        <Link to={linkTo(`/chronicle/${mainTop.slug}`)} className="block">
                          <div className="aspect-[16/9] overflow-hidden bg-gray-100">
                            <img
                              src={mainTop.cover}
                              alt={mainTop.title}
                              loading="eager"
                              decoding="async"
                              fetchPriority="low"
                              className="h-full w-full object-cover group-hover:scale-[1.01] transition-transform"
                              onError={(e) => {
                                const img = e.currentTarget as HTMLImageElement;
                                img.onerror = null;
                                img.src = getImageUrl('placeholder.svg');
                                img.style.display = 'block';
                              }}
                            />
                          </div>
                          {/* Breaking news ticker under hero image */}
                          <div className="bg-red-600 text-white overflow-hidden">
                            <div className="whitespace-nowrap py-1 uppercase ticker-anim">
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                              <span className="mx-6 font-semibold tracking-wide">Breaking News •</span>
                            </div>
                          </div>
                          <div className="p-6">
                            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight text-gray-900 group-hover:text-blue-800" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              <span className="inline-flex items-baseline align-baseline mr-3 text-red-600 font-extrabold leading-none whitespace-nowrap">
                                <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-600 mr-2 animate-pulse relative -top-[1px]"></span>
                                LIVE
                              </span>
                              {mainTop.title}
                            </h2>
                            {mainTop.excerpt && (
                              <p className="mt-3 text-gray-700 text-base md:text-lg" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {mainTop.excerpt}
                              </p>
                            )}
                            <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                              <span>{mainTop.author}</span>
                              <span>{new Date(mainTop.date).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </Link>
                      </article>
                    </div>

                    {/* Right column (2 smaller articles stacked) */}
                    <div className="lg:col-span-3 hidden lg:flex flex-col gap-6">
                      {rightPosts.map((rp) => (
                        <article key={rp.slug} className="group rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                          <Link to={linkTo(`/chronicle/${rp.slug}`)}>
                            <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                              <img
                                src={rp.cover}
                                alt={rp.title}
                                loading="lazy"
                                decoding="async"
                                fetchPriority="low"
                                className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform"
                                onError={(e) => {
                                  const img = e.currentTarget as HTMLImageElement;
                                  img.onerror = null;
                                  img.src = getImageUrl('placeholder.svg');
                                  img.style.display = 'block';
                                }}
                              />
                            </div>
                            <div className="p-4">
                              <h3
                                className="text-base font-semibold text-gray-900 group-hover:text-blue-700"
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                              >
                                {(rp.section || '') === 'Top Stories' && (
                                  <span className="inline-flex items-baseline align-baseline mr-2 text-red-600 font-semibold text-xs leading-none whitespace-nowrap">
                                    <span className="inline-block w-2 h-2 rounded-full bg-red-600 mr-1 animate-pulse relative -top-[1px]"></span>
                                    LIVE
                                  </span>
                                )}
                                {rp.title}
                              </h3>
                              <p
                                className="mt-1 text-gray-600 text-sm"
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                              >
                                {rp.excerpt || ''}
                              </p>
                              <div className="mt-3 text-xs text-gray-500 flex items-center justify-between">
                                <span>{rp.author}</span>
                                <span>{new Date(rp.date).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </Link>
                        </article>
                      ))}
                    </div>

                    {/* Mobile/tablet side articles */}
                    <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {[...leftPosts, ...rightPosts].map((sp) => (
                        <article key={sp.slug} className="group rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                          <Link to={linkTo(`/chronicle/${sp.slug}`)}>
                            <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                              <img
                                src={sp.cover}
                                alt={sp.title}
                                loading="lazy"
                                decoding="async"
                                fetchPriority="low"
                                className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform"
                                onError={(e) => {
                                  const img = e.currentTarget as HTMLImageElement;
                                  img.onerror = null;
                                  img.src = getImageUrl('placeholder.svg');
                                  img.style.display = 'block';
                                }}
                              />
                            </div>
                            <div className="p-4">
                              <h3
                                className="text-base font-semibold text-gray-900 group-hover:text-blue-700"
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                              >
                                {(sp.section || '') === 'Top Stories' && (
                                  <span className="inline-flex items-baseline align-baseline mr-2 text-red-600 font-semibold text-xs leading-none whitespace-nowrap">
                                    <span className="inline-block w-2 h-2 rounded-full bg-red-600 mr-1 animate-pulse relative -top-[1px]"></span>
                                    LIVE
                                  </span>
                                )}
                                {sp.title}
                              </h3>
                              <p
                                className="mt-1 text-gray-600 text-sm"
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                              >
                                {sp.excerpt || ''}
                              </p>
                              <div className="mt-3 text-xs text-gray-500 flex items-center justify-between">
                                <span>{sp.author}</span>
                                <span>{new Date(sp.date).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </Link>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Remaining posts below */}
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="grid items-stretch gap-6 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3">
                  {remainingPosts.map((p) => (
                    <article key={p.slug} className="group rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden h-full flex flex-col">
                      <Link to={linkTo(`/chronicle/${p.slug}`)} className="flex h-full flex-col">
                        <div className="aspect-[16/9] overflow-hidden bg-gray-100">
                          <img
                            src={p.cover}
                            alt={p.title}
                            loading="lazy"
                            decoding="async"
                            fetchPriority="low"
                            className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform"
                            onError={(e) => {
                              const img = e.currentTarget as HTMLImageElement;
                              img.onerror = null;
                              img.src = getImageUrl('placeholder.svg');
                              img.style.display = 'block';
                            }}
                          />
                        </div>
                        <div className="p-5 flex flex-col flex-1">
                          <div className="flex flex-wrap gap-2 mb-3">
                            {(p.tags || []).map((t) => (
                              <span key={t} className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-gray-600">
                                {t}
                              </span>
                            ))}
                          </div>
                          <h3
                            className="text-lg font-semibold text-gray-900 group-hover:text-blue-700"
                            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                          >
                            {(p.section || '') === 'Top Stories' && (
                              <span className="inline-flex items-baseline align-baseline mr-2 text-red-600 font-semibold text-xs leading-none whitespace-nowrap">
                                <span className="inline-block w-2 h-2 rounded-full bg-red-600 mr-1 animate-pulse relative -top-[1px]"></span>
                                LIVE
                              </span>
                            )}
                            {p.title}
                          </h3>
                          <p
                            className="mt-2 text-gray-600 text-sm"
                            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                          >
                            {p.excerpt || ''}
                          </p>
                          <div className="mt-auto pt-3 sm:pt-4 flex items-center justify-between text-xs text-gray-500">
                            <span>{p.author}</span>
                            <span>{new Date(p.date).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>
              </div>
            </>
          );
        })()}
      </section>

      <TenantFooter />
    </div>
  );
}

